import { makeAutoObservable, runInAction } from "mobx";
import apiHandler from "../handlers/ApiHandler";

class UserStore {
  allUsers = [];
  loading = false;

  constructor() {
    makeAutoObservable(this);
  }

  getUserNameById(id) {
    console.log("Here is all user: ", this.allUsers);
    const user = this.allUsers.find((u) => u.id === id);
    return user?.name || user?.email || id;
  }

  async fetchAllUsers() {
    this.loading = true;

    try {
      const data = await apiHandler.get("/api/profile/retrieve-all-users");
      console.log("All user data: ", data);

      const parsedUsers = data.map((u) => ({
        id: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        city: u.city,
        state: u.state,
        country: u.country,
        roles: u.roles ? u.roles.split(",").map((r) => r.trim()) : [],
        permissions: u.permissions
          ? u.permissions.split(",").map((p) => p.trim())
          : [],
        emailVerified: Boolean(u.emailVerified),
        active: Boolean(u.active),
        createdDate: new Date(u.createdDate).toLocaleString(),
        updatedDate: new Date(u.updatedDate).toLocaleString(),
      }));

      runInAction(() => {
        this.allUsers = parsedUsers;
        this.loading = false;
      });

      return parsedUsers; // ✅ return parsed list so it's awaitable
    } catch (e) {
      console.error("Failed fetching users", e);
      runInAction(() => {
        this.loading = false;
      });
      return []; // ✅ always return an array
    }
  }

  async createUser(newUser) {
    try {
      const createdUser = await apiHandler.post("/api/auth/signup", newUser);
      const normalized = {
        id: createdUser.id,
        name: createdUser.name,
        email: createdUser.email,
        phone: createdUser.phone ?? "",
        city: createdUser.city ?? "",
        state: createdUser.state ?? "",
        country: createdUser.country ?? "",
        emailVerified: Boolean(createdUser.verified),
        active: Boolean(createdUser.active),
        createdDate: new Date(createdUser.createdDate).toLocaleString(),
        updatedDate: new Date(createdUser.updatedDate).toLocaleString(),
      };
      runInAction(() => {
        this.allUsers = [...this.allUsers, normalized];
      });
      return normalized;
    } catch (e) {
      console.error("Failed to create user", e);
      throw e;
    }
  }

  async updateUser(updatedUser) {
    if (!updatedUser.id) {
      throw new Error("User ID is required for update!");
    }

    try {
      const payload = {};

      if (updatedUser.email !== undefined) payload.username = updatedUser.email;
      if (updatedUser.name !== undefined) payload.name = updatedUser.name;
      if (updatedUser.phone !== undefined) payload.phone = updatedUser.phone;
      if (updatedUser.city !== undefined) payload.city = updatedUser.city;
      if (updatedUser.state !== undefined) payload.state = updatedUser.state;
      if (updatedUser.country !== undefined)
        payload.country = updatedUser.country;

      if (updatedUser.emailVerified !== undefined)
        payload.verified = updatedUser.emailVerified ? 1 : 0;
      if (updatedUser.active !== undefined)
        payload.active = updatedUser.active ? 1 : 0;
      if (Array.isArray(updatedUser.roles)) payload.roles = updatedUser.roles;
      if (Array.isArray(updatedUser.permissions))
        payload.permissions = updatedUser.permissions;

      const response = await apiHandler.put(
        `/api/profile/update-user/${updatedUser.id}`,
        payload
      );

      const normalized = {
        id: response.id,
        email: response.email,
        name: response.name,
        phone: response.phone ?? "",
        city: response.city ?? "",
        state: response.state ?? "",
        country: response.country ?? "",
        createdDate: new Date(response.createdDate).toLocaleString(),
        updatedDate: new Date(response.updatedDate).toLocaleString(),
        emailVerified: response.verified === 1,
        active: Boolean(response.active),
        roles: response.roles
          ? response.roles.split(",").map((r) => r.trim())
          : [],
        permissions: response.permissions
          ? response.permissions.split(",").map((p) => p.trim())
          : [],
      };

      this.allUsers = this.allUsers.map((u) =>
        u.id === normalized.id ? normalized : u
      );

      return normalized;
    } catch (e) {
      console.error("Failed updating user", e);
      throw e;
    }
  }

  async fetchAllRoles() {
    try {
      const roles = await apiHandler.get("/api/profile/all-roles");
      return roles;
    } catch (e) {
      console.error("Failed fetching roles", e);
      throw e;
    }
  }

  async fetchAllPermissions() {
    try {
      const permissions = await apiHandler.get("/api/profile/all-permissions");
      return permissions;
    } catch (e) {
      console.error("Failed fetching permissions", e);
      throw e;
    }
  }

  // async fetchRolePermLists() {
  //   try {
  //     const [roles, permissions] = await Promise.all([
  //       apiHandler.get("/api/all-roles"),
  //       apiHandler.get("/api/all-permissions"),
  //     ]);
  //     return { roles, permissions };
  //   } catch (e) {
  //     console.error("Failed fetching role & permission lists", e);
  //     throw e;
  //   }
  // }

  async fetchRolePermLists() {
    const results = await Promise.allSettled([
      apiHandler.get("/api/profile/all-roles"),
      apiHandler.get("/api/profile/all-permissions"),
    ]);

    results.forEach((result, i) => {
      if (result.status === "rejected") {
        console.error(
          i === 0 ? "❌ Roles fetch failed:" : "❌ Permissions fetch failed:",
          result.reason
        );
      }
    });

    const roles = results[0].status === "fulfilled" ? results[0].value : [];
    const permissions =
      results[1].status === "fulfilled" ? results[1].value : [];

    if (results.some((r) => r.status === "rejected")) {
      console.error("Partial failure fetching roles/permissions:", results);
    }
    return { roles, permissions };
  }

  async deleteUser(id) {
    try {
      await apiHandler.delete(`/api/profile/delete-user/${id}`);
      runInAction(() => {
        this.allUsers = this.allUsers.filter((u) => u.id !== id);
      });
    } catch (e) {
      console.error("Failed deleting user", e);
      throw e;
    }
  }
}

const userStore = new UserStore();
export default userStore;
